import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Divider,
  useTheme,
  IconButton,
} from '@mui/material';
import { Search, X } from 'lucide-mui';
import { LanguageCode, topLanguagesSorted } from '../translate';
import ActionDialog from '../../globalModals/ActionDialog';

// Common languages that most users need
const COMMON_LANGUAGES: LanguageCode[] = [
  'en',
  'es',
  'fr',
  'de',
  'zh-CN',
  'ja',
  'ko',
  'pt',
  'ru',
  'hi',
];

interface LanguageDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (language: LanguageCode) => void;
  isTranslating: boolean;
  title?: string;
}

const LanguageDialog: React.FC<LanguageDialogProps> = ({
  open,
  onClose,
  onSelect,
  isTranslating,
  title = 'Translate',
}) => {
  const theme = useTheme();
  const [languageFilter, setLanguageFilter] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Focus search input when dialog opens
  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  // Reset selection when dialog opens/closes
  useEffect(() => {
    setSelectedIndex(-1);
  }, [open]);

  const filteredLanguages = languageFilter
    ? topLanguagesSorted.filter(
        (lang) =>
          lang.name.toLowerCase().includes(languageFilter.toLowerCase()) ||
          lang.code.toLowerCase().includes(languageFilter.toLowerCase())
      )
    : topLanguagesSorted;

  const commonLanguages = topLanguagesSorted.filter((lang) =>
    COMMON_LANGUAGES.includes(lang.code)
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const currentList = languageFilter ? filteredLanguages : commonLanguages;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((prev) =>
        prev < 0 ? 0 : Math.min(prev + 1, currentList.length - 1)
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (event.key === 'Enter' && selectedIndex >= 0) {
      onSelect(currentList[selectedIndex].code);
    }
  };

  const renderLanguageList = (
    languages: typeof topLanguagesSorted,
    isCommonList = false
  ) => (
    <List
      dense
      sx={{
        pt: 0,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 0.5,
      }}
    >
      {languages.map((language, index) => {
        const isSelected = !languageFilter
          ? isCommonList && index === selectedIndex
          : index === selectedIndex;

        return (
          <ListItem
            key={language.code}
            button
            selected={isSelected}
            onClick={() => onSelect(language.code)}
            sx={{
              borderRadius: 1,
              py: 0.75,
              px: 1.5,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
              '&.Mui-selected': {
                backgroundColor: 'action.selected',
                '&:hover': {
                  backgroundColor: 'action.selected',
                },
              },
            }}
          >
            <ListItemText
              primary={language.name}
              secondary={language.code.toUpperCase()}
              primaryTypographyProps={{
                variant: 'body2',
                sx: {
                  fontWeight: 500,
                  color: 'text.primary',
                },
              }}
              secondaryTypographyProps={{
                sx: {
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  mt: 0,
                },
              }}
            />
          </ListItem>
        );
      })}
    </List>
  );

  return (
    <ActionDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title={title}
      actions={[]}
    >
      <Box sx={{ pt: 1, px: { xs: 2, sm: 3 } }}>
        {isTranslating ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap={2}
            minHeight={150}
          >
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Translating...
            </Typography>
          </Box>
        ) : (
          <>
            <Paper
              elevation={0}
              sx={{
                mb: 2,
                backgroundColor: 'background.default',
                borderRadius: '20px',
                height: '40px',
                border: 'none',
              }}
            >
              <TextField
                inputRef={searchInputRef}
                placeholder="Search languages..."
                variant="outlined"
                fullWidth
                size="small"
                value={languageFilter}
                onKeyDown={handleKeyDown}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 20, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: languageFilter ? (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setLanguageFilter('');
                          setSelectedIndex(-1);
                          searchInputRef.current?.focus();
                        }}
                        edge="end"
                        size="small"
                        sx={{
                          mr: -0.5,
                          color: 'text.secondary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        }}
                      >
                        <X strokeWidth={2} style={{ width: 18, height: 18 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                onChange={(e) => {
                  setLanguageFilter(e.target.value);
                  setSelectedIndex(-1);
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '100%',
                    borderRadius: '20px',
                    '& fieldset': {
                      borderColor: 'transparent',
                    },
                    '&:hover fieldset': {
                      borderColor: 'transparent',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'transparent',
                    },
                    '& .MuiInputAdornment-root': {
                      '& .MuiSvgIcon-root': {
                        fontSize: '20px',
                        color: 'text.secondary',
                        transition: 'color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                      },
                    },
                    '&:hover .MuiInputAdornment-root .MuiSvgIcon-root': {
                      color: 'text.primary',
                    },
                  },
                }}
              />
            </Paper>

            <Box
              sx={{
                maxHeight: '60vh',
                overflow: 'auto',
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: theme.palette.divider,
                  borderRadius: '3px',
                },
              }}
            >
              {!languageFilter && (
                <>
                  <Typography
                    variant="overline"
                    sx={{
                      color: 'text.secondary',
                      display: 'block',
                      px: 1,
                      mb: 1,
                      fontSize: '0.7rem',
                      letterSpacing: '0.1em',
                    }}
                  >
                    Common Languages
                  </Typography>
                  {renderLanguageList(commonLanguages, true)}

                  <Divider sx={{ my: 2 }} />

                  <Typography
                    variant="overline"
                    sx={{
                      color: 'text.secondary',
                      display: 'block',
                      px: 1,
                      mb: 1,
                      fontSize: '0.7rem',
                      letterSpacing: '0.1em',
                    }}
                  >
                    All Languages
                  </Typography>
                </>
              )}
              {renderLanguageList(filteredLanguages, false)}
            </Box>
          </>
        )}
      </Box>
    </ActionDialog>
  );
};

export default LanguageDialog;
